import React, { useState, useEffect } from 'react';
import {
    X,
    GraduationCap,
    Sparkles,
    Camera,
    Package,
    Tag,
    Lightbulb,
    Target,
    MessageSquare,
    Heart,
    Users,
    Zap,
    TrendingUp,
    Plus,
    Trash2,
    Info,
    Wand2,
    Loader2,
    ArrowRight
} from 'lucide-react';
import {
    DEFAULT_PILLAR,
    AWARENESS_STAGES,
    FUNNEL_STAGES,
    PILLAR_PRESETS
} from '../../services/pillarService';
import { generatePillarFromDescription } from '../../services/strategyAIService';
import ConfirmationModal from '../ConfirmationModal';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

/**
 * Available icons for pillars
 */
const AVAILABLE_ICONS = [
    { name: 'GraduationCap', icon: GraduationCap, label: 'Education' },
    { name: 'Sparkles', icon: Sparkles, label: 'Entertainment' },
    { name: 'Camera', icon: Camera, label: 'Behind the Scenes' },
    { name: 'Package', icon: Package, label: 'Product' },
    { name: 'Tag', icon: Tag, label: 'Offers' },
    { name: 'Lightbulb', icon: Lightbulb, label: 'Ideas' },
    { name: 'Target', icon: Target, label: 'Target' },
    { name: 'MessageSquare', icon: MessageSquare, label: 'Social' },
    { name: 'Heart', icon: Heart, label: 'Lifestyle' },
    { name: 'Users', icon: Users, label: 'Community' },
    { name: 'Zap', icon: Zap, label: 'Action' },
    { name: 'TrendingUp', icon: TrendingUp, label: 'Growth' }
];

/**
 * Preset color palette
 */
const COLOR_PALETTE = [
    '#10B981', // Green
    '#F59E0B', // Amber
    '#8B5CF6', // Purple
    '#3B82F6', // Blue
    '#EC4899', // Pink
    '#EF4444', // Red
    '#6366F1', // Indigo
    '#14B8A6', // Teal
    '#F97316', // Orange
    '#84CC16'  // Lime
];

/**
 * PillarEditor Modal Component
 * Create/edit a marketing pillar with full configuration
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {function} props.onClose - Callback to close modal
 * @param {function} props.onSave - Callback to save pillar
 * @param {Object} props.pillar - Existing pillar to edit (null for new)
 * @param {Object} props.brand - Brand data for AI generation
 * @param {boolean} props.loading - Whether save is in progress
 */
const PillarEditor = ({
    isOpen,
    onClose,
    onSave,
    pillar = null,
    brand = null,
    loading = false
}) => {
    const [formData, setFormData] = useState({ ...DEFAULT_PILLAR });
    const [activeTab, setActiveTab] = useState('basics');
    const [newTopic, setNewTopic] = useState('');
    const [newTone, setNewTone] = useState('');
    const [newMessage, setNewMessage] = useState('');

    // AI Assistant state
    const [showAIAssistant, setShowAIAssistant] = useState(false);
    const [aiDescription, setAiDescription] = useState('');
    const [aiGenerating, setAiGenerating] = useState(false);
    const [aiError, setAiError] = useState(null);

    const { captureInitialState, showUnsavedModal, setShowUnsavedModal, handleCloseAttempt } = useUnsavedChanges(formData);

    // Initialize form with pillar data or defaults
    useEffect(() => {
        let initialData;
        if (pillar) {
            initialData = { ...DEFAULT_PILLAR, ...pillar };
            setFormData(initialData);
            setShowAIAssistant(false);
        } else {
            initialData = { ...DEFAULT_PILLAR };
            setFormData(initialData);
            // Show AI assistant by default for new pillars
            setShowAIAssistant(true);
        }
        captureInitialState(initialData);
        setActiveTab('basics');
        setAiDescription('');
        setAiError(null);
    }, [pillar, isOpen]);

    const handleAttemptClose = () => {
        if (!handleCloseAttempt()) {
            onClose();
        }
    };

    const handleSaveAndClose = () => {
        if (!formData.name.trim()) {
            setShowUnsavedModal(false);
            return;
        }
        onSave(formData);
        setShowUnsavedModal(false);
    };

    const handleDiscardAndClose = () => {
        setShowUnsavedModal(false);
        onClose();
    };

    // Handle AI pillar generation
    const handleAIGenerate = async () => {
        if (!aiDescription.trim()) return;

        setAiGenerating(true);
        setAiError(null);

        try {
            const generatedPillar = await generatePillarFromDescription(brand || {}, aiDescription);

            // Apply generated pillar to form
            setFormData(prev => ({
                ...prev,
                ...generatedPillar
            }));

            // Hide AI assistant and show results
            setShowAIAssistant(false);
            setAiDescription('');
        } catch (error) {
            console.error('AI generation failed:', error);
            setAiError('Failed to generate pillar. Please try again or create manually.');
        } finally {
            setAiGenerating(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleNestedChange = (parent, field, value) => {
        setFormData(prev => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [field]: value
            }
        }));
    };

    const handleAwarenessChange = (stage, field, value) => {
        setFormData(prev => ({
            ...prev,
            awarenessMapping: {
                ...prev.awarenessMapping,
                [stage]: {
                    ...prev.awarenessMapping?.[stage],
                    [field]: value
                }
            }
        }));
    };

    const addListItem = (parent, field, value, setValue) => {
        if (!value.trim()) return;
        setFormData(prev => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [field]: [...(prev[parent]?.[field] || []), value.trim()]
            }
        }));
        setValue('');
    };

    const removeListItem = (parent, field, index) => {
        setFormData(prev => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [field]: prev[parent]?.[field]?.filter((_, i) => i !== index) || []
            }
        }));
    };

    const applyPreset = (preset) => {
        setFormData(prev => ({
            ...prev,
            name: preset.name,
            description: preset.description,
            color: preset.color,
            icon: preset.icon,
            awarenessMapping: preset.awarenessMapping,
            contentGuidelines: {
                ...prev.contentGuidelines,
                ...preset.contentGuidelines
            }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return;
        onSave(formData);
    };

    return (
        <>
            {/* Backdrop */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 9998,
                    opacity: isOpen ? 1 : 0,
                    pointerEvents: isOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease'
                }}
                onClick={handleAttemptClose}
            />
            {/* Panel */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    maxWidth: '560px',
                    background: 'var(--color-bg-primary)',
                    boxShadow: isOpen ? '-4px 0 24px rgba(0, 0, 0, 0.15)' : 'none',
                    zIndex: 9999,
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div style={{
                    padding: 'var(--space-4) var(--space-5)',
                    borderBottom: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--radius-md)',
                            background: `${formData.color}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {(() => {
                                const IconComp = AVAILABLE_ICONS.find(i => i.name === formData.icon)?.icon || Lightbulb;
                                return <IconComp size={20} style={{ color: formData.color }} />;
                            })()}
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>
                                {pillar ? 'Edit Pillar' : 'Create Pillar'}
                            </h2>
                            <p style={{
                                margin: 0,
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                Define a content theme for your marketing strategy
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleAttemptClose}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--space-2)' }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Tabs */}
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-1)',
                    padding: 'var(--space-2) var(--space-5)',
                    borderBottom: '1px solid var(--color-border)',
                    background: 'var(--color-bg-secondary)',
                    flexShrink: 0
                }}>
                    {[
                        { id: 'basics', label: 'Basics' },
                        { id: 'awareness', label: 'Awareness Stages' },
                        { id: 'guidelines', label: 'Content Guidelines' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`btn btn-sm ${activeTab === tab.id ? 'btn-primary' : 'btn-ghost'}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-5)' }}>

                        {/* Basics Tab */}
                        {activeTab === 'basics' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                {/* AI Assistant for new pillars */}
                                {!pillar && showAIAssistant && (
                                    <div style={{
                                        padding: 'var(--space-4)',
                                        background: 'linear-gradient(135deg, var(--primary-50) 0%, var(--info-50) 100%)',
                                        borderRadius: 'var(--radius-lg)',
                                        border: '1px solid var(--primary-200)'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            marginBottom: 'var(--space-3)'
                                        }}>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: 'var(--radius-md)',
                                                background: 'var(--color-primary)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Wand2 size={18} style={{ color: 'white' }} />
                                            </div>
                                            <div>
                                                <h4 style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>AI Pillar Assistant</h4>
                                                <p style={{
                                                    margin: 0,
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-secondary)'
                                                }}>
                                                    Describe your pillar idea and AI will fill in the details
                                                </p>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: 'var(--space-3)' }}>
                                            <textarea
                                                value={aiDescription}
                                                onChange={(e) => setAiDescription(e.target.value)}
                                                placeholder="Describe the content pillar you want to create... (e.g., 'Educational content that teaches customers how to use our products effectively' or 'Behind-the-scenes content showing our team and company culture')"
                                                className="input"
                                                style={{
                                                    width: '100%',
                                                    minHeight: '80px',
                                                    resize: 'vertical'
                                                }}
                                                disabled={aiGenerating}
                                            />
                                        </div>

                                        {aiError && (
                                            <div style={{
                                                padding: 'var(--space-2) var(--space-3)',
                                                background: 'var(--error-50)',
                                                borderRadius: 'var(--radius-md)',
                                                color: 'var(--error-700)',
                                                fontSize: 'var(--font-size-sm)',
                                                marginBottom: 'var(--space-3)'
                                            }}>
                                                {aiError}
                                            </div>
                                        )}

                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <button
                                                type="button"
                                                onClick={() => setShowAIAssistant(false)}
                                                className="btn btn-ghost btn-sm"
                                                disabled={aiGenerating}
                                            >
                                                Skip, create manually
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleAIGenerate}
                                                className="btn btn-primary btn-sm"
                                                disabled={!aiDescription.trim() || aiGenerating}
                                                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                                            >
                                                {aiGenerating ? (
                                                    <>
                                                        <Loader2 size={14} className="spin" />
                                                        Generating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Sparkles size={14} />
                                                        Generate Pillar
                                                        <ArrowRight size={14} />
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Show "Use AI" button if manually creating */}
                                {!pillar && !showAIAssistant && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAIAssistant(true)}
                                        className="btn btn-ghost"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            justifyContent: 'center',
                                            padding: 'var(--space-3)',
                                            background: 'var(--color-bg-secondary)',
                                            border: '1px dashed var(--color-border)'
                                        }}
                                    >
                                        <Wand2 size={16} style={{ color: 'var(--color-primary)' }} />
                                        Use AI to help create this pillar
                                    </button>
                                )}

                                {/* Presets */}
                                {!pillar && !showAIAssistant && (
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 500,
                                            marginBottom: 'var(--space-2)'
                                        }}>
                                            Start from a preset (optional)
                                        </label>
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 'var(--space-2)'
                                        }}>
                                            {PILLAR_PRESETS.map(preset => (
                                                <button
                                                    key={preset.name}
                                                    type="button"
                                                    onClick={() => applyPreset(preset)}
                                                    className="btn btn-sm"
                                                    style={{
                                                        background: formData.name === preset.name ? `${preset.color}20` : 'var(--color-bg-tertiary)',
                                                        borderColor: formData.name === preset.name ? preset.color : 'transparent',
                                                        color: formData.name === preset.name ? preset.color : 'var(--color-text-primary)'
                                                    }}
                                                >
                                                    {preset.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Name */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        Pillar Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        placeholder="e.g., Education, Entertainment, Social Proof"
                                        className="input"
                                        style={{ width: '100%' }}
                                        required
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                        placeholder="Describe what type of content falls under this pillar..."
                                        className="input"
                                        style={{ width: '100%', minHeight: '80px', resize: 'vertical' }}
                                    />
                                </div>

                                {/* Color */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        Color
                                    </label>
                                    <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                                        {COLOR_PALETTE.map(color => (
                                            <button
                                                key={color}
                                                type="button"
                                                onClick={() => handleChange('color', color)}
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: 'var(--radius-md)',
                                                    background: color,
                                                    border: formData.color === color ? '3px solid var(--color-text-primary)' : '2px solid transparent',
                                                    cursor: 'pointer',
                                                    transition: 'transform var(--transition-fast)'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Icon */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        Icon
                                    </label>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(6, 1fr)',
                                        gap: 'var(--space-2)'
                                    }}>
                                        {AVAILABLE_ICONS.map(({ name, icon: Icon, label }) => (
                                            <button
                                                key={name}
                                                type="button"
                                                onClick={() => handleChange('icon', name)}
                                                title={label}
                                                style={{
                                                    padding: 'var(--space-2)',
                                                    borderRadius: 'var(--radius-md)',
                                                    background: formData.icon === name ? `${formData.color}20` : 'var(--color-bg-tertiary)',
                                                    border: formData.icon === name ? `2px solid ${formData.color}` : '2px solid transparent',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Icon
                                                    size={20}
                                                    style={{
                                                        color: formData.icon === name ? formData.color : 'var(--color-text-secondary)'
                                                    }}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Content Weight */}
                                {pillar && (
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 500,
                                            marginBottom: 'var(--space-2)'
                                        }}>
                                            Content Weight
                                        </label>
                                        <p style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-secondary)',
                                            marginBottom: 'var(--space-2)'
                                        }}>
                                            Percentage of content allocated to this pillar. All active pillars should total 100%.
                                        </p>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={formData.frequency?.currentWeight || 0}
                                                onChange={(e) => handleNestedChange('frequency', 'currentWeight', parseInt(e.target.value))}
                                                style={{ flex: 1 }}
                                            />
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-1)',
                                                minWidth: '60px'
                                            }}>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={formData.frequency?.currentWeight || 0}
                                                    onChange={(e) => {
                                                        const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                                        handleNestedChange('frequency', 'currentWeight', val);
                                                    }}
                                                    className="input"
                                                    style={{ width: '60px', textAlign: 'center' }}
                                                />
                                                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>%</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Awareness Stages Tab */}
                        {activeTab === 'awareness' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                <div style={{
                                    padding: 'var(--space-3)',
                                    background: 'var(--info-50)',
                                    borderRadius: 'var(--radius-md)',
                                    display: 'flex',
                                    gap: 'var(--space-2)'
                                }}>
                                    <Info size={16} style={{ color: 'var(--info-600)', flexShrink: 0, marginTop: '2px' }} />
                                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--info-700)' }}>
                                        <strong>Customer Awareness Journey:</strong> Customers progress from not knowing they have a problem
                                        to being ready to buy. Select which stages this pillar best serves and set its weight (importance)
                                        for each stage.
                                    </div>
                                </div>

                                {Object.entries(AWARENESS_STAGES).map(([stage, info]) => (
                                    <div
                                        key={stage}
                                        style={{
                                            padding: 'var(--space-4)',
                                            background: 'var(--color-bg-secondary)',
                                            borderRadius: 'var(--radius-md)',
                                            border: formData.awarenessMapping?.[stage]?.recommended
                                                ? `2px solid ${formData.color}`
                                                : '2px solid transparent'
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            marginBottom: 'var(--space-3)'
                                        }}>
                                            <div>
                                                <label style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-2)',
                                                    cursor: 'pointer'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.awarenessMapping?.[stage]?.recommended || false}
                                                        onChange={(e) => handleAwarenessChange(stage, 'recommended', e.target.checked)}
                                                        style={{ width: '18px', height: '18px' }}
                                                    />
                                                    <span style={{ fontWeight: 600 }}>{info.label}</span>
                                                </label>
                                                <p style={{
                                                    margin: 'var(--space-1) 0 0 26px',
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-secondary)'
                                                }}>
                                                    {info.description}
                                                </p>
                                            </div>
                                        </div>

                                        {formData.awarenessMapping?.[stage]?.recommended && (
                                            <div style={{ marginLeft: '26px' }}>
                                                <label style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-2)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}>
                                                    <span>Weight:</span>
                                                    <input
                                                        type="range"
                                                        min="0"
                                                        max="100"
                                                        value={formData.awarenessMapping?.[stage]?.weight || 0}
                                                        onChange={(e) => handleAwarenessChange(stage, 'weight', parseInt(e.target.value))}
                                                        style={{ flex: 1 }}
                                                    />
                                                    <span style={{ width: '40px', textAlign: 'right', fontWeight: 500 }}>
                                                        {formData.awarenessMapping?.[stage]?.weight || 0}%
                                                    </span>
                                                </label>
                                                <p style={{
                                                    margin: 'var(--space-1) 0 0 0',
                                                    fontSize: '11px',
                                                    color: 'var(--color-text-tertiary)'
                                                }}>
                                                    Typical content: {info.contentFocus}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Content Guidelines Tab */}
                        {activeTab === 'guidelines' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                                {/* Tone of Voice */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        Tone of Voice
                                    </label>
                                    <p style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-secondary)',
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        How should content in this pillar sound? (e.g., informative, playful, urgent)
                                    </p>
                                    <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                        <input
                                            type="text"
                                            value={newTone}
                                            onChange={(e) => setNewTone(e.target.value)}
                                            placeholder="Add a tone..."
                                            className="input"
                                            style={{ flex: 1 }}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem('contentGuidelines', 'toneOfVoice', newTone, setNewTone))}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => addListItem('contentGuidelines', 'toneOfVoice', newTone, setNewTone)}
                                            className="btn btn-secondary"
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                        {formData.contentGuidelines?.toneOfVoice?.map((tone, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-1)',
                                                    padding: 'var(--space-1) var(--space-2)',
                                                    background: 'var(--color-bg-tertiary)',
                                                    borderRadius: 'var(--radius-full)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}
                                            >
                                                {tone}
                                                <button
                                                    type="button"
                                                    onClick={() => removeListItem('contentGuidelines', 'toneOfVoice', idx)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '2px',
                                                        display: 'flex'
                                                    }}
                                                >
                                                    <X size={12} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Example Topics */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        Example Topics
                                    </label>
                                    <p style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-secondary)',
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        What specific topics or content ideas fit this pillar?
                                    </p>
                                    <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                        <input
                                            type="text"
                                            value={newTopic}
                                            onChange={(e) => setNewTopic(e.target.value)}
                                            placeholder="Add a topic..."
                                            className="input"
                                            style={{ flex: 1 }}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem('contentGuidelines', 'exampleTopics', newTopic, setNewTopic))}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => addListItem('contentGuidelines', 'exampleTopics', newTopic, setNewTopic)}
                                            className="btn btn-secondary"
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                        {formData.contentGuidelines?.exampleTopics?.map((topic, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-1)',
                                                    padding: 'var(--space-1) var(--space-2)',
                                                    background: 'var(--color-bg-tertiary)',
                                                    borderRadius: 'var(--radius-full)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}
                                            >
                                                {topic}
                                                <button
                                                    type="button"
                                                    onClick={() => removeListItem('contentGuidelines', 'exampleTopics', idx)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '2px',
                                                        display: 'flex'
                                                    }}
                                                >
                                                    <X size={12} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Key Messages */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        Key Messages
                                    </label>
                                    <p style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-secondary)',
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        Core messages that should be reinforced in this pillar's content
                                    </p>
                                    <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                        <input
                                            type="text"
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            placeholder="Add a key message..."
                                            className="input"
                                            style={{ flex: 1 }}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem('contentGuidelines', 'keyMessages', newMessage, setNewMessage))}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => addListItem('contentGuidelines', 'keyMessages', newMessage, setNewMessage)}
                                            className="btn btn-secondary"
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                        {formData.contentGuidelines?.keyMessages?.map((message, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: 'var(--space-2) var(--space-3)',
                                                    background: 'var(--color-bg-tertiary)',
                                                    borderRadius: 'var(--radius-md)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}
                                            >
                                                <span>{message}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeListItem('contentGuidelines', 'keyMessages', idx)}
                                                    className="btn btn-ghost btn-sm"
                                                    style={{ padding: 'var(--space-1)' }}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div style={{
                        padding: 'var(--space-4) var(--space-5)',
                        borderTop: '1px solid var(--color-border)',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 'var(--space-3)',
                        flexShrink: 0
                    }}>
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-ghost"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading || !formData.name.trim()}
                        >
                            {loading ? 'Saving...' : pillar ? 'Save Changes' : 'Create Pillar'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Unsaved Changes Confirmation */}
            <ConfirmationModal
                isOpen={showUnsavedModal}
                onClose={() => setShowUnsavedModal(false)}
                onConfirm={handleSaveAndClose}
                onSecondaryAction={handleDiscardAndClose}
                title="Unsaved Changes"
                message="You have unsaved changes. Would you like to save them before closing?"
                confirmText="Save Changes"
                secondaryActionText="Discard Changes"
                secondaryVariant="danger"
                cancelText="Keep Editing"
                variant="primary"
            />
        </>
    );
};

export default PillarEditor;
